import { useMemo } from "react";
import {
  ProgramData,
  IOptionalStandard,
  ICertificateInfo,
  IUserProgramCertificate,
  OnboardingCertificateIssueDate,
} from "@interfaces/Interfaces";
import {
  canSaveDetails as canSaveDetailsUtil,
  getOptionalStandardsForSelectedRole,
  ValidationState,
} from "@utils/programDetails/programDetailsValidation";

interface UseProgramDetailsValidationReturn {
  canSaveDetails: boolean;
  optionalStandardsForSelectedRole: IOptionalStandard[];
}

/**
 * Hook for program details validation logic
 */
export const useProgramDetailsValidation = (
  program: ProgramData,
  state: {
    selectedPrimaryRoles: string[];
    selectedStandards: IOptionalStandard[];
    declarationChecked: boolean;
    selectedArea: string;
    selectedTeam: string;
    uploads: ICertificateInfo[];
    deletions: ICertificateInfo[];
    existingCertificates: IUserProgramCertificate[];
    issueDates: OnboardingCertificateIssueDate[];
    appraisalDate: Date | null;
  }
): UseProgramDetailsValidationReturn => {
  // Memoize the validation state to prevent unnecessary recalculations
  const validationState: ValidationState = useMemo(
    () => ({
      selectedPrimaryRoles: state.selectedPrimaryRoles,
      selectedStandards: state.selectedStandards,
      declarationChecked: state.declarationChecked,
      selectedArea: state.selectedArea,
      selectedTeam: state.selectedTeam,
      uploads: state.uploads,
      deletions: state.deletions,
      existingCertificates: state.existingCertificates,
      issueDates: state.issueDates,
      appraisalDate: state.appraisalDate,
    }),
    [
      state.selectedPrimaryRoles,
      state.selectedStandards,
      state.declarationChecked,
      state.selectedArea,
      state.selectedTeam,
      state.uploads,
      state.deletions,
      state.existingCertificates,
      state.issueDates,
      state.appraisalDate,
    ]
  );

  // Memoize the validation result
  const canSaveDetails = useMemo(() => {
    return canSaveDetailsUtil(validationState, program);
  }, [validationState, program]);

  // Memoize optional standards calculation
  const optionalStandardsForSelectedRole = useMemo(() => {
    return getOptionalStandardsForSelectedRole(state.selectedPrimaryRoles, program);
  }, [state.selectedPrimaryRoles, program]);

  return {
    canSaveDetails,
    optionalStandardsForSelectedRole,
  };
};
